import type { ReactNode } from 'react';
import { Box, Group, Stack, Text } from '@mantine/core';

// Page header with a slanted, full-height colored status banner on the right.
// Shared by the Offer and Invoice pages for a consistent status presentation.
// The banner can carry a description and an extra slot beneath the status label.
export function StatusHeaderBar({
  title,
  subtitles,
  actions,
  statusLabel,
  color = 'gray',
  description,
  statusExtra,
  underLayer,
}: {
  title: ReactNode;
  subtitles?: ReactNode;
  actions?: ReactNode;
  statusLabel: string;
  /** Mantine color key (e.g. 'green', 'blue', 'red', 'teal', 'gray'). */
  color?: string;
  /** Detail text shown inside the status banner, under the status label. */
  description?: ReactNode;
  /** Slot for extra elements in the banner (e.g. an action button). */
  statusExtra?: ReactNode;
  /**
   * A second status layer stacked beneath the banner, peeking out as a slanted
   * strip (e.g. a yellow "Archived" line under the main status).
   */
  underLayer?: { label: string; icon?: ReactNode; color?: string };
}) {
  const hasBannerBody = !!description || !!statusExtra;

  return (
    <Box style={{ position: 'relative', overflow: 'hidden' }}>
      <Group justify="space-between" align="stretch" wrap="nowrap" gap={0}>
        <Stack gap="sm" py="xs" style={{ flex: 1, minWidth: 0 }}>
          <Box>
            {typeof title === 'string' ? (
              <Text fz="xl" fw={700}>
                {title}
              </Text>
            ) : (
              title
            )}
            {subtitles}
          </Box>
          {actions && (
            <Group gap="xs" wrap="wrap">
              {actions}
            </Group>
          )}
        </Stack>

        <Box style={{ position: 'relative', alignSelf: 'stretch' }}>
          {underLayer && (
            <Box
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                // The band content sits at mid-height, where the banner's
                // diagonal has advanced to ~28px; an 84px-wide band leaves the
                // 72px content block a small pad from both slanted edges for
                // any realistic banner height.
                left: -84,
                zIndex: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                gap: 12,
                paddingLeft: 34,
                paddingTop: 16,
                paddingBottom: 16,
                background: `var(--mantine-color-${
                  underLayer.color ?? 'yellow'
                }-5)`,
                color: 'var(--mantine-color-white)',
                clipPath: 'polygon(56px 0, 100% 0, 100% 100%, 0 100%)',
              }}
            >
              <Box
                style={{
                  width: 72,
                  height: 44,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {underLayer.icon}
              </Box>
              <Text fz="sm" ta="center" style={{ width: 72 }}>
                {underLayer.label}
              </Text>
            </Box>
          )}
          <Box
            style={{
              position: 'relative',
              zIndex: 1,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-end',
              gap: 12,
              minWidth: 240,
              paddingLeft: 96,
              paddingRight: 40,
              paddingTop: 16,
              paddingBottom: 16,
              background: `var(--mantine-color-${color}-7)`,
              color: 'var(--mantine-color-white)',
              clipPath: 'polygon(56px 0, 100% 0, 100% 100%, 0 100%)',
            }}
          >
            <Text
              fz={40}
              fw={400}
              style={{ lineHeight: 1.1, whiteSpace: 'nowrap' }}
            >
              {statusLabel}
            </Text>
            {hasBannerBody && (
              <Stack gap={10} align="flex-end" style={{ maxWidth: 460 }}>
                {description &&
                  (typeof description === 'string' ? (
                    <Text fz="sm" ta="right">
                      {description}
                    </Text>
                  ) : (
                    description
                  ))}
                {statusExtra}
              </Stack>
            )}
          </Box>
        </Box>
      </Group>
    </Box>
  );
}

export default StatusHeaderBar;
